import useSWR from "swr";
import { AiccLanguageStatus } from "constants/aiccLanguage";
import * as aiccPromptAPI from "services/aiccPrompt";
import { AICC_SWR_CONFIG } from "./swrConfig";

/**
 * One prompt with its variables and reference configs (AICC2-328).
 *
 * Read fresh rather than taken from the row that was clicked: the list carries no text, no variables
 * and no default, and the editor cannot be opened on a partial answer.
 *
 * Writes are not SWR-managed — the caller saves through the service and then `mutate`s. The response
 * of a save is the new truth, so a caller with it in hand can pass it to `mutate` and skip the re-read.
 */
export function useAiccPrompt(languageId?: number, promptKey?: string, status?: AiccLanguageStatus) {
  const { data, error, isLoading, mutate } = useSWR(
    /**
     * 🔴 `status` IS PART OF THE KEY, NOT JUST OF THE REQUEST. The two copies of one prompt are two
     * answers from one endpoint, and `AICC_SWR_CONFIG` dedupes for five seconds: sharing a key means
     * opening the published row, going back and opening the draft row within that window renders the
     * badge of the copy before it. Five seconds is the normal speed of going back and clicking — the same
     * accident `useAiccPrompts.ts` already records for the list.
     */
    languageId && promptKey ? ["aicc/prompt", languageId, promptKey, status] : null,
    () => aiccPromptAPI.fetchAiccPrompt(languageId as number, promptKey as string, status),
    AICC_SWR_CONFIG
  );

  return {
    prompt: data,
    isLoading,
    isError: !!error,
    mutate,
  };
}
